/** Форматирование чисел и времени для интерфейса. Всё на русском. */

const NBSP = ' ';

/** Русское склонение: plural(5, 'литр', 'литра', 'литров'). */
export function plural(
  n: number,
  one: string,
  few: string,
  many: string,
): string {
  const abs = Math.abs(Math.round(n)) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return many;
  if (last > 1 && last < 5) return few;
  if (last === 1) return one;
  return many;
}

/** 12345 → «12 345» (неразрывные пробелы, чтобы число не рвалось по строкам). */
export function formatNumber(value: number, fractionDigits = 0): string {
  return value
    .toFixed(fractionDigits)
    .replace(/\B(?=(\d{3})+(?!\d))/g, NBSP)
    .replace('.', ',');
}

/** Секунды → «1 ч 20 мин» / «12 мин» / «40 с». */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  const total = Math.round(seconds);
  if (total < 60) return `${total}${NBSP}с`;
  const minutes = Math.round(total / 60);
  if (minutes < 60) return `${minutes}${NBSP}мин`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0
    ? `${hours}${NBSP}ч`
    : `${hours}${NBSP}ч${NBSP}${rest}${NBSP}мин`;
}

/** Метры → «820 м» / «3,4 км». */
export function formatDistance(meters: number): string {
  if (!Number.isFinite(meters) || meters < 0) return '—';
  if (meters < 950) return `${Math.round(meters / 10) * 10}${NBSP}м`;
  return `${formatNumber(meters / 1000, 1)}${NBSP}км`;
}

/** Литры с одним знаком после запятой, без хвостового нуля. */
export function formatLiters(liters: number): string {
  const rounded = Math.round(liters * 10) / 10;
  const text = Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toFixed(1).replace('.', ',');
  return `${text}${NBSP}л`;
}

/** ISO → «14:35». */
export function formatClock(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/** «через 12 мин» относительно текущего момента. */
export function formatRelative(iso: string, now: number = Date.now()): string {
  const date = new Date(iso).getTime();
  if (Number.isNaN(date)) return '—';
  const deltaS = (date - now) / 1000;
  if (deltaS <= 30) return 'сейчас';
  return `через ${formatDuration(deltaS)}`;
}

/** Секунды → «мм:сс» для таймера повторной отправки кода. */
export function formatCountdown(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${mm}:${String(ss).padStart(2, '0')}`;
}

/** +7 (999) 123-45-67 из произвольного ввода. */
export function formatPhone(digits: string): string {
  const d = digits.replace(/\D/g, '').slice(0, 11);
  if (d.length === 0) return '';
  const rest = d[0] === '7' || d[0] === '8' ? d.slice(1) : d;
  const parts = [
    rest.slice(0, 3),
    rest.slice(3, 6),
    rest.slice(6, 8),
    rest.slice(8, 10),
  ].filter((part) => part.length > 0);

  let out = '+7';
  if (parts[0]) out += ` (${parts[0]}`;
  // Скобку закрываем только когда за ней уже есть цифры — иначе backspace
  // упирается в неё и удаление выглядит сломанным.
  if (parts[0] && parts[0].length === 3 && rest.length > 3) out += ')';
  if (parts[1]) out += ` ${parts[1]}`;
  if (parts[2]) out += `-${parts[2]}`;
  if (parts[3]) out += `-${parts[3]}`;
  return out;
}

/** Только цифры номера в формате 7XXXXXXXXXX. */
export function normalizePhone(input: string): string {
  const d = input.replace(/\D/g, '');
  if (d.length === 0) return '';
  if (d[0] === '8') return `7${d.slice(1, 11)}`;
  if (d[0] === '7') return d.slice(0, 11);
  return `7${d.slice(0, 10)}`;
}
